/**
 * System notifications bell — AppBar icon + popover for non-messaging notifications.
 *
 * The messaging unread count is handled by a separate MessagingInboxPopover.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuardedNavigate } from '../../shared/hooks/useGuardedNavigate';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, Inbox } from 'lucide-react';
import { DisclosureChevron } from '../../shared/components/common';
import { Button } from '../ui/button';
import { Popover } from '../ui/popover';
import {
  useInboxNotifications,
  useInboxNotificationSearch,
  useInboxUnreadCount,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  notificationsInboxKeys,
  InboxNotification,
  InboxSearch,
} from '../../shared/notifications/useNotificationsInbox';
import { useNotificationHandler } from '../../shared/notifications/useNotificationHandler';
import { applyNotificationDeepLinkTab } from '../../shared/notifications/deepLinkTab';
import { notificationActionUrl } from '../../shared/notifications/actionUrl';
import {
  GroupedReminder,
  parseGroupedReminder,
} from '../../shared/notifications/groupedReminder';
import { entityUrlContext, getEntityUrl } from '../../shared/utils/entityUrls';
import { filterAndSortByPrioritizedSearch } from '../../shared/utils/searchScore';
import { COMMENT_ENTITY_TYPE_TO_ENTITY_TYPE } from '../../generated/entity-vocabulary';
import {
  localizedNotificationTitle,
  notificationTypesMatchingQuery,
} from '../../shared/notifications/notificationTitle';
import { useResponsive } from '../../shared/hooks/useResponsive';
import { useDebounce, useRelativeDate } from '../../shared/hooks';
import {
  AppBarBadgeButton,
  AppBarPopoverList,
  AppBarPopoverItem,
  UpcomingRemindersSection,
} from './appBarPopover';
import { useUpcomingReminders } from '../../shared/notifications/useEntityReminders';
import {
  indexActiveReminders,
  useActiveReminders,
} from '../../shared/notifications/useNagReminders';
import {
  hasReminderActions,
  interactionDialogTarget,
  readReminderActionTarget,
  type ReminderActionTarget,
} from '../../shared/notifications/reminderActions';
import { ReminderActionStrip } from '../../shared/notifications/ReminderActionStrip';
import { InteractionFormDialog } from '../../shared/components/interactions/InteractionFormDialog';
import { useAuth } from '../../shared/auth/AuthContext';

// Notification types handled by the messaging badge, not this bell.
// Mentions and member_added intentionally fall through to the standard bell.
const MESSAGING_TYPES = new Set<string>([
  'message.new',
  'message.updated',
  'message.deleted',
  'conversation.created',
  'conversation.updated',
  'conversation.member_removed',
  'typing.indicator',
]);

/** Rows fetched while the reader is just glancing at the bell. */
const RESTING_INBOX_LIMIT = 30;

/**
 * Rows fetched while a filter is active — the inbox route's own `limit` ceiling.
 *
 * Two pages ride at this size while the reader is filtering, and both are load-
 * bearing. The SEARCH page is what reaches past the newest rows at all. The
 * RECENT one is kept because the client's own matcher is finer than any SQL
 * predicate — a four-letter typo shares too few trigrams for the server's fuzzy
 * arm — so leaving it in the candidate union is what makes this change a strict
 * superset of what the bell could already find, rather than a trade.
 */
const SEARCHING_INBOX_LIMIT = 100;

/**
 * How long the reader may pause before the search page is re-fetched.
 *
 * Filtering now costs a request, so a page per keystroke would be a request per
 * keystroke. Short enough that the wait is not felt on the pause between words;
 * the previous query's matches stay on screen throughout (`keepPreviousData`).
 */
const SEARCH_DEBOUNCE_MS = 250;

interface SystemNotificationsBellProps {
  moduleColor: string;
}

/**
 * Where a bell row navigates: the entity deep link, else `data.action_url`.
 *
 * An alert whose subject is not an addressable entity (a usage quota, an expiring
 * OAuth grant) carries no `entity_type` at all and links through `action_url`
 * instead — the same field the backend email CTA already resolves. The fallback is
 * second, so an entity link always wins, and it goes through the shared
 * `notificationActionUrl` guard shared with the toast handler and mobile push:
 * `navigate()` on an absolute or protocol-relative URL is an open redirect.
 */
export function notificationHref(n: InboxNotification): string | null {
  return entityHref(n) ?? notificationActionUrl(n.data);
}

function entityHref(n: InboxNotification): string | null {
  if (!n.entity_type || !n.entity_id) return null;
  // Some notification types deep-link to a specific tab (e.g. a new ticket
  // message → Messages, a comment mention → Comments). Centralized in
  // applyNotificationDeepLinkTab so the bell, web toast handler, mobile push and
  // the backend email CTA stay in sync.
  //
  // The cases below are LEGACY bare-noun entity_types persisted in the inbox
  // before the emit sites named real tables; new rows fall through to the Entity
  // URL Registry, which is what every other surface already resolves against.
  switch (n.entity_type) {
    case 'item':
      return `/tasks/item/${n.entity_id}`;
    case 'deal':
      return applyNotificationDeepLinkTab(
        `/pipeline/deals/${n.entity_id}`,
        n.notification_type,
      );
    // 'ticket' = legacy inbox rows persisted before the support catalog landed;
    // 'support.ticket' = the canonical entity_type emitted now.
    case 'ticket':
    case 'support.ticket':
      return applyNotificationDeepLinkTab(
        `/support/tickets/${n.entity_id}`,
        n.notification_type,
      );
    // Sysadmin platform alerts open the Alerts page focused on the alert
    // (AlertsListPage reads ?alert_id=…). entity_id is the alert_id.
    case 'sysadmin_alert':
      return `/sysadmin/alerts?alert_id=${n.entity_id}`;
    case 'shipment':
      return `/shipments/${n.entity_id}?tab=tracking`;
    case 'conversation':
      return `/messaging?conversation=${n.entity_id}`;
    case 'message': {
      const conversationId = n.data?.conversation_id;
      return typeof conversationId === 'string'
        ? `/messaging?conversation=${conversationId}`
        : null;
    }
    default: {
      // Comment notifications emitted before the fan-out crossed the entity
      // vocabulary bridge persisted the BARE COMMENT NOUN ("contact",
      // "organization", "candidate") as entity_type, and those nouns are in the
      // `entity_comments.entity_type` space, not the URL registry's — so those
      // inbox rows resolve to nothing and the row is inert. Translate them here
      // through the same generated map the backend now uses, so history clicks
      // through too. New rows already carry the canonical key and pass straight
      // through (the map has no canonical keys to collide with).
      const entityType =
        COMMENT_ENTITY_TYPE_TO_ENTITY_TYPE[n.entity_type] ?? n.entity_type;
      const url = getEntityUrl({
        entityType,
        entityId: n.entity_id,
        context: entityUrlContext(n.data),
        // Inbox rows are persisted, so this runs over historical entity_types too.
        // A convention-guessed URL for one of those is worse than no link at all.
      });
      return url ? applyNotificationDeepLinkTab(url, n.notification_type) : null;
    }
  }
}

export function SystemNotificationsBell({ moduleColor }: SystemNotificationsBellProps) {
  const { t } = useTranslation(['common', 'notifications']);
  const formatRelative = useRelativeDate();
  // Clicking a notification is the user choosing a destination, so it owes a
  // dirty form the same prompt a sidebar entry does. (The open-redirect check
  // on `href` below is unchanged and still runs first.)
  const navigate = useGuardedNavigate();
  const [open, setOpen] = useState(false);
  // Which grouped rows the reader has opened. Reset with the popover, so it never
  // reopens half-expanded from a session the reader has moved on from.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());
  const { isMobile } = useResponsive();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // What the reader is filtering by. The ranking below is client-side, but the
  // CANDIDATES are no longer whatever page happened to be loaded: the bell is the
  // only global notifications surface, so a filter that could reach no further
  // than the newest rows reported a real notification as absent, which the reader
  // has no way to detect. An active query asks the server to search instead.
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebounce(trimmedQuery, SEARCH_DEBOUNCE_MS);
  const { data: unreadCount = 0 } = useInboxUnreadCount();
  const fetchLimit = query ? SEARCHING_INBOX_LIMIT : RESTING_INBOX_LIMIT;
  const { data: inboxPage, isLoading } = useInboxNotifications(fetchLimit);
  // The stored title is English and the row shows the localized catalog label, so
  // the types whose LABEL matches are something only this side can resolve — the
  // server admits them alongside its own text match.
  const search = useMemo<InboxSearch | null>(
    () =>
      debouncedQuery
        ? { query: debouncedQuery, types: notificationTypesMatchingQuery(t, debouncedQuery) }
        : null,
    [debouncedQuery, t],
  );
  const { data: matchPage, isFetching: isSearchFetching } =
    useInboxNotificationSearch(search, SEARCHING_INBOX_LIMIT);

  // The candidate set is the union: the server's matches from anywhere in the
  // inbox, plus the recent page the bell holds anyway. Newest-first, because the
  // ranking below sorts stably and an inbox's tiebreak is recency — the server
  // orders ITS page by relevance, which is a different question (which matches
  // survive its own limit).
  const notifications = useMemo(() => {
    const recent = inboxPage?.items ?? [];
    if (!trimmedQuery) return recent;
    const matched = matchPage?.items ?? [];
    const matchedIds = new Set(matched.map((n) => n.id));
    return [...matched, ...recent.filter((n) => !matchedIds.has(n.id))].sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );
  }, [inboxPage, matchPage, trimmedQuery]);

  // More MATCHES than the server could return in one page — the only bound left
  // now that the search is not confined to a window of recent rows.
  const searchIsTruncated =
    trimmedQuery.length > 0 && (matchPage?.total ?? 0) > (matchPage?.items.length ?? 0);
  // A server answer is what makes "no matches" true, so never say it while one is
  // still coming: an in-flight search with nothing on screen is loading, not empty.
  const searchIsSettled =
    !trimmedQuery || (debouncedQuery === trimmedQuery && !isSearchFetching);
  // Reminders that have not fired yet are not inbox rows — they become notifications
  // only when the sweep dispatches them — so they are a second read, fetched only while
  // the popover is open rather than on every page the app bar renders on.
  const { data: upcomingData } = useUpcomingReminders({ enabled: open });
  // The nags' LIVE state. An inbox row is an emit-time snapshot, so a row that was
  // snoozed or resolved since it was sent still describes the world before the reader
  // acted — this is what the strip reconciles against. Fetched only while the popover
  // is open, like the reminders above.
  const { data: activeReminders } = useActiveReminders({ enabled: open });
  const activeById = useMemo(() => indexActiveReminders(activeReminders), [activeReminders]);
  // The "Log an activity" dialog is owned by the BELL, not by the strip that offers it:
  // a modal portals to `body`, so a pointerdown inside it is an outside pointerdown to
  // the Radix popover — which closes, unmounting the strip and the dialog with it. Held
  // here, the popover closing is harmless and even desirable.
  const [interactionTarget, setInteractionTarget] = useState<ReminderActionTarget | null>(
    null,
  );
  // The reminders query is enabled only on open, so on the first open it is always
  // behind the inbox one — which may answer instantly from cache. Without counting it
  // as loading, a reader with reminders but no unread notifications sees "You're all
  // caught up" flash and then get replaced by their own reminders.
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Filtering ranks a SUBJECT hit above a body one and tolerates small typos —
  // both come from the shared search scorer, which is what every other
  // search-as-you-type surface in the app is built on. The title is matched as the
  // reader SEES it (localized), not as the raw stored string.
  const visibleNotifications = useMemo(
    () =>
      filterAndSortByPrioritizedSearch(notifications, query, {
        primary: (n) => localizedNotificationTitle(t, n.notification_type, n.title),
        secondary: (n) => {
          const group = parseGroupedReminder(n.data);
          // Match what the row actually shows: a grouped reminder's subtitle is its
          // entity names, not the stored digest table.
          return group ? group.items.map((item) => item.label).join(' ') : n.body ?? '';
        },
      }),
    [notifications, query, t],
  );

  const visibleReminders = useMemo(
    () =>
      filterAndSortByPrioritizedSearch(upcomingData?.items ?? [], query, {
        primary: (reminder) => reminder.entity_title ?? '',
        secondary: (reminder) => reminder.note ?? '',
      }),
    [upcomingData, query],
  );

  const isLoadingAny =
    isLoading ||
    (open && upcomingData === undefined) ||
    // Nothing on screen and the server's answer still coming: report it as
    // loading rather than as "no matching notifications", which is the very
    // false negative this whole change exists to remove.
    (!searchIsSettled && visibleNotifications.length === 0 && visibleReminders.length === 0);

  const isFiltering = query.length > 0;
  // The search now covers the whole inbox, so a miss is a miss — there is no
  // window left to qualify it with.
  const noMatchMessage = t('notifications.noSearchResults');

  // RETAINED: the bell ledger is the alert stream's OWN store, and this is the
  // one place where receiving an alert *is* the data change — per recipient,
  // which no tenant+module cue can express. The freshness layer would refetch
  // every connected user's inbox for a notification addressed to one of them.
  useNotificationHandler({
    '*': {
      onNotification: (n) => {
        if (MESSAGING_TYPES.has(n.type)) return;
        queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
      },
    },
  });

  // `&& !open` because `handleClick` marks a row read BEFORE it toggles: on a phone,
  // expanding the last unread reminder drops the count to zero and would tear down the
  // popover the reader is mid-interaction with.
  if (isMobile && unreadCount === 0 && !open) return null;

  /** One notification read as an action target — the row's own entity refs. */
  const reminderTarget = (n: InboxNotification): ReminderActionTarget =>
    readReminderActionTarget({
      data: n.data,
      entityType: n.entity_type,
      entityId: n.entity_id,
    });

  const toggleExpanded = (id: string) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const goTo = (href: string) => {
    // Close the popover so it doesn't linger over the destination page — an
    // in-app navigate() isn't an outside-click, so Radix won't auto-dismiss.
    closePopover();
    navigate(href);
  };

  const handleClick = (n: InboxNotification, group: GroupedReminder | null) => {
    if (!n.read_at) markRead.mutate(n.id);
    // A grouped reminder's row is a DISCLOSURE, not a link: it stands for N
    // entities, and picking one of them for the reader would be a guess. The
    // entities below it are the links.
    if (group) {
      toggleExpanded(n.id);
      return;
    }
    // A NAG discloses its own options instead of navigating — the customer complaint
    // this answers is that the only thing the row could do was throw them into the
    // record. "Open" is one of the buttons inside. Every other row still navigates.
    if (hasReminderActions(reminderTarget(n))) {
      toggleExpanded(n.id);
      return;
    }
    const href = notificationHref(n);
    if (href) {
      goTo(href);
    }
  };

  /**
   * Close the popover and drop the per-session state that goes with it.
   *
   * Both exits come through here, because Radix's `onOpenChange` fires only for
   * dismissals IT owns — an in-app `navigate()` is not one, so a row click that
   * only called `setOpen(false)` would leave the filter (and the open groups)
   * behind, and the next open would silently hide the reader's newest alerts
   * behind a query they had forgotten typing.
   */
  const closePopover = () => {
    setOpen(false);
    setExpandedIds(new Set());
    setQuery('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      closePopover();
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
      <AppBarBadgeButton
        icon={<Bell className="h-4 w-4" />}
        count={unreadCount}
        ariaLabel={t('notifications.bellAriaLabel')}
        tooltip={t('notifications.bellTooltip')}
        moduleColor={moduleColor}
      />
      <AppBarPopoverList
        title={t('notifications.title')}
        headerAction={
          unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <Check className="h-3.5 w-3.5" />
              {t('notifications.markAllRead')}
            </Button>
          ) : undefined
        }
        search={{
          value: query,
          onChange: setQuery,
          placeholder: t('notifications.searchPlaceholder'),
        }}
        isLoading={isLoadingAny}
        // The shared empty state REPLACES the children, so it may only win when BOTH
        // sections are empty — otherwise "you're all caught up" would hide a reminder
        // the user has scheduled for this afternoon.
        isEmpty={visibleNotifications.length === 0 && visibleReminders.length === 0}
        emptyIcon={<Inbox className="h-8 w-8" />}
        emptyMessage={isFiltering ? noMatchMessage : t('notifications.empty')}
      >
        {visibleReminders.length > 0 && (
          <UpcomingRemindersSection
            reminders={visibleReminders}
            currentUserId={user?.id ?? null}
            onNavigate={goTo}
          />
        )}

        {visibleNotifications.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {isFiltering ? noMatchMessage : t('notifications.empty')}
          </p>
        )}

        {visibleNotifications.map((n) => {
          const group = parseGroupedReminder(n.data);
          const expanded = expandedIds.has(n.id);
          const target = group ? null : reminderTarget(n);
          const isNag = target !== null && hasReminderActions(target);
          return (
            <div key={n.id}>
              <AppBarPopoverItem
                onClick={() => handleClick(n, group)}
                unread={!n.read_at}
                title={localizedNotificationTitle(t, n.notification_type, n.title)}
                subtitle={
                  group
                    ? // The stored body is the digest's whole "Label: value — url"
                      // table, which reads as noise in two clamped lines. The names
                      // alone are the useful preview; the links live in the
                      // expansion below. Comma-joined, not middot-joined: a rule's
                      // own label may itself use a middot (pipeline's is
                      // "Name · Milestone"), and two middot levels read as one
                      // undifferentiated run of words.
                      group.items.map((item) => item.label).join(', ')
                    : n.body || undefined
                }
                meta={formatRelative(new Date(n.created_at))}
                expanded={group || isNag ? expanded : undefined}
                controlsId={`notification-group-${n.id}`}
                trailing={
                  group || isNag ? (
                    <DisclosureChevron open={expanded} className="text-muted-foreground" />
                  ) : undefined
                }
              />
              {isNag && expanded && target && (
                <ReminderActionStrip
                  id={`notification-group-${n.id}`}
                  target={target}
                  activeReminder={
                    target.reminderStateId
                      ? activeById.get(target.reminderStateId)
                      : undefined
                  }
                  onNavigate={goTo}
                  onLogActivity={setInteractionTarget}
                />
              )}
              {group && expanded && (
                <ul id={`notification-group-${n.id}`} className="border-b bg-muted/40 py-1">
                  {group.items.map((item) => {
                    // A digest row's own nag, so the strip acts on the entity the
                    // reader expanded rather than on the first one of the burst.
                    const itemTarget = readReminderActionTarget({
                      data: n.data,
                      entityType: item.entityType,
                      entityId: item.entityId,
                      reminderStateIdOverride: item.reminderStateId,
                    });
                    const itemKey = `${n.id}:${item.entityType}:${item.entityId}`;
                    const itemExpanded = expandedIds.has(itemKey);
                    const itemIsNag = hasReminderActions(itemTarget);
                    return (
                      <li key={`${item.entityType}:${item.entityId}`}>
                        <Button
                          variant="ghost"
                          className="h-auto w-full justify-start rounded-none px-3 py-1.5 text-left text-xs font-normal text-foreground"
                          // A row with neither its own nag nor a destination is inert.
                          // A digest emitted before per-item ids shipped has no nag, so
                          // it keeps the original navigate-on-click behaviour.
                          disabled={!itemIsNag && !item.path}
                          aria-expanded={itemIsNag ? itemExpanded : undefined}
                          aria-controls={itemIsNag ? `notification-item-${itemKey}` : undefined}
                          onClick={() => {
                            if (itemIsNag) {
                              toggleExpanded(itemKey);
                              return;
                            }
                            if (item.path) goTo(item.path);
                          }}
                        >
                          <span className="truncate">{item.label}</span>
                          {itemIsNag && (
                            <DisclosureChevron
                              open={itemExpanded}
                              className="ms-auto text-muted-foreground"
                            />
                          )}
                        </Button>
                        {itemIsNag && itemExpanded && (
                          <ReminderActionStrip
                            id={`notification-item-${itemKey}`}
                            target={itemTarget}
                            activeReminder={
                              itemTarget.reminderStateId
                                ? activeById.get(itemTarget.reminderStateId)
                                : undefined
                            }
                            onNavigate={goTo}
                            onLogActivity={setInteractionTarget}
                          />
                        )}
                      </li>
                    );
                  })}
                  {group.remaining > 0 && (
                    <li className="px-3 py-1.5 text-xs text-muted-foreground">
                      {t('notifications.grouped.more', { count: group.remaining })}
                    </li>
                  )}
                </ul>
              )}
            </div>
          );
        })}

        {searchIsTruncated && visibleNotifications.length > 0 && (
          <p className="px-3 py-2 text-center text-[11px] text-muted-foreground">
            {/* Counts what is ON SCREEN, not what the server returned: the rows
                are the union of the server's page with any recent row that
                matched only on this side, so the server's own page size would
                undercount the list the reader is looking at. */}
            {t('notifications.searchScopeMatches', {
              count: visibleNotifications.length,
            })}
          </p>
        )}
      </AppBarPopoverList>
      </Popover>

      {/* Mounted OUTSIDE the popover deliberately — see `ReminderActionStrip`'s header:
          a modal portals to `body`, so opening one from inside the popover is an outside
          pointerdown that unmounts the strip that opened it. Here the popover may close
          freely and the dialog survives. */}
{(() => {
        // The id mapping lives in `reminderActions` so it is a value a test can
        // read — as a ternary pair here, swapping the two arms rendered an
        // identical dialog against nothing.
        const dialog = interactionTarget && interactionDialogTarget(interactionTarget);
        return dialog ? (
          <InteractionFormDialog
            open
            onClose={() => setInteractionTarget(null)}
            {...dialog}
          />
        ) : null;
      })()}
    </>
  );
}
